/**
 * Layout Component
 *
 * Main application layout with sidebar, main panel, and header.
 * Provides the shell for the authenticated application experience.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { getCustomerName, getProductName, useRuntimeConfigStore } from '@/stores/runtimeConfigStore';
import { useFileStore, FileViewerType } from '@/stores/fileStore';
import { useChatStore } from '@/stores/chatStore';
import { usePinsStore } from '@/stores/pinsStore';
import { api, type ClaudeAuthStatus, type CodexAuthStatus, type Pin, type ProducedItem, type StorageInfo, type SystemStatus, type WorkspaceAsset } from '@/services/api';
import { Button } from '@/components/ui/button';
import { FileTree } from '@/components/FileTree';
import { FileTabs } from '@/components/FileTabs';
import { FileUpload } from '@/components/FileUpload';
import { ChatSessionSidebar } from '@/components/ChatSessionSidebar';
import { ChatContainer } from '@/components/ChatContainer';
import { SettingsPanel } from '@/components/SettingsPanel';
import { SkillsCatalog } from '@/components/SkillsCatalog';
import { SipnavApiMapModal } from '@/components/SipnavApiMapModal';
import { PinToggle } from '@/components/PinToggle';
import { downloadWorkspaceFile } from '@/lib/downloadFile';
import {
  CodeViewer,
  CsvViewer,
  ExcelViewer,
  HtmlViewer,
  MarkdownEditor,
  ImageViewer,
  PdfViewer,
  PcapViewer,
  ParquetViewer,
  BinaryViewer,
} from '@/components/viewers';
import { cn } from '@/lib/utils';
import {
  PanelLeftClose,
  PanelLeft,
  FolderOpen,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Upload,
  Loader2,
  AlertCircle,
  HardDrive,
  FileText,
  Activity,
  RefreshCw,
  PanelRight,
  PanelRightClose,
  ChevronDown,
  Bookmark,
  Sparkles,
  RotateCcw,
  Search,
  Maximize2,
  Minimize2,
  Download,
  BookOpen,
  Lock,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface LayoutProps {
  children?: React.ReactNode;
}

type SidebarTab = 'chat' | 'files';
type FilePanelMode = 'minimized' | 'expanded';
type SettingsSection = 'app' | 'claude' | 'switches' | 'traces' | 'security' | 'about' | 'users' | 'training' | 'skills';

// ============================================
// TelcoOS Logo Component
// ============================================

function TelcoOSLogo({ className }: { className?: string }) {
  return <BrandLogo className={className} />;
}

// ============================================
// Header Component
// ============================================

interface HeaderProps {
  onMenuClick: () => void;
  isSidebarOpen: boolean;
  onSettingsClick: () => void;
  onStatusClick: () => void;
  onSipnavApiMapClick: () => void;
}

function Header({
  onMenuClick,
  isSidebarOpen,
  onSettingsClick,
  onStatusClick,
  onSipnavApiMapClick,
}: HeaderProps) {
  const { user, logout, isLoading } = useAuthStore();
  const {
    config: runtimeConfig,
    initialized: runtimeConfigInitialized,
    error: runtimeConfigError,
  } = useRuntimeConfigStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const avatarLetter = user?.username?.charAt(0).toUpperCase() || '?';
  const productName = getProductName(runtimeConfig);
  const customerName = runtimeConfigInitialized && !runtimeConfigError
    ? getCustomerName(runtimeConfig)
    : null;

  return (
    <header className="flex h-12 shrink-0 items-center justify-between border-b border-surface-200 bg-white px-4 dark:border-surface-800 dark:bg-surface-900">
      <div className="flex min-w-0 items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
          aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {isSidebarOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </Button>

        <div className="flex min-w-0 items-center gap-2">
          <TelcoOSLogo className="h-7 w-7 shrink-0 rounded-md" />
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="font-display truncate text-lg font-semibold text-surface-900 dark:text-surface-100">
              {customerName || productName}
            </span>
            {customerName && (
              <span className="truncate text-[11px] font-medium uppercase tracking-wide text-surface-500 dark:text-surface-400">
                {productName}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {user?.isSupportAdmin && runtimeConfig?.primaryProviderAdapterKey === 'sipnav' && (
          <Button
            variant="ghost"
            size="icon"
            title="SipNav API Map"
            aria-label="Open SipNav API Map"
            onClick={onSipnavApiMapClick}
          >
            <BookOpen className="h-5 w-5" />
          </Button>
        )}
        <Button variant="ghost" size="icon" title="Status" onClick={onStatusClick}>
          <Activity className="h-5 w-5" />
        </Button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowUserMenu((open) => !open)}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-surface-700 transition-colors',
              'hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800',
              showUserMenu && 'bg-surface-100 dark:bg-surface-800'
            )}
            title={user?.username}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-800 text-sm font-medium text-white dark:bg-surface-200 dark:text-surface-900">
              {avatarLetter}
            </span>
            <span className="hidden md:inline">{user?.username}</span>
            <ChevronDown className="hidden h-4 w-4 text-surface-500 md:inline" />
          </button>

          {showUserMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
              <div className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-800">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    onSettingsClick();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-700"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    logout();
                  }}
                  disabled={isLoading}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-surface-100 disabled:opacity-60 dark:text-red-400 dark:hover:bg-surface-700"
                >
                  <LogOut className="h-4 w-4" />
                  {isLoading ? 'Signing out...' : 'Sign Out'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ============================================
// Sidebar Component
// ============================================

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  onUploadClick: (destination?: string, onSuccess?: () => void | Promise<void>) => void;
  onCollapse: () => void;
}

function Sidebar({
  isOpen,
  isCollapsed,
  activeTab,
  onTabChange,
  onUploadClick,
  onCollapse,
}: SidebarProps) {
  const [storage, setStorage] = useState<StorageInfo | null>(null);

  // Fetch storage info
  useEffect(() => {
    const fetchStorage = async () => {
      try {
        const info = await api.system.getStorage();
        setStorage(info);
      } catch (err) {
        console.error('Failed to fetch storage:', err);
      }
    };

    fetchStorage();
    // Refresh every 60 seconds
    const interval = setInterval(fetchStorage, 60000);
    return () => clearInterval(interval);
  }, []);

  // Get storage bar color based on percentage
  const getStorageColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-primary-500';
  };

  return (
    <aside
      className={cn(
        'relative flex h-full flex-col border-r border-surface-200 bg-white dark:border-surface-800 dark:bg-surface-900',
        // Mobile: slide in/out
        'fixed bottom-0 left-0 top-12 z-40 lg:relative lg:inset-auto',
        'lg:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        // Width based on collapsed state (use fixed width when collapsed)
        isCollapsed ? 'w-16' : 'w-[300px]',
        // Only animate when not resizing
        'transition-all duration-200'
      )}
    >
      <div className={cn('relative border-b border-surface-200 dark:border-surface-800', isCollapsed && 'p-2')}>
        <div className={cn('flex', isCollapsed && 'flex-col gap-1')}>
          <button
            type="button"
            onClick={() => onTabChange('files')}
            className={cn(
              'relative flex flex-1 items-center justify-center gap-2 py-2.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40',
              activeTab === 'files'
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200'
            )}
            title="Files"
          >
            <FolderOpen className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Files</span>}
            {activeTab === 'files' && !isCollapsed && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600 dark:bg-primary-400" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onTabChange('chat')}
            className={cn(
              'relative flex flex-1 items-center justify-center gap-2 py-2.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40',
              activeTab === 'chat'
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200'
            )}
            title="Chat Sessions"
          >
            <MessageSquare className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Chat</span>}
            {activeTab === 'chat' && !isCollapsed && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600 dark:bg-primary-400" />
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {!isCollapsed && activeTab === 'chat' && (
          <ChatSessionSidebar isCollapsed={false} />
        )}
        {!isCollapsed && activeTab === 'files' && (
          <FileTree onUploadClick={onUploadClick} />
        )}
        {isCollapsed && activeTab === 'files' && (
          <div className="flex flex-col gap-2 p-2">
            <Button variant="ghost" size="icon" className="w-full" onClick={() => onUploadClick()} title="Upload files">
              <Upload className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="border-t border-surface-200 dark:border-surface-800">
        {storage && (
          <div className="px-3 py-3">
            {isCollapsed ? (
              // Collapsed: just show icon with color
              <div
                className="flex items-center justify-center"
                title={`${storage.usedFormatted} / ${storage.limitFormatted} (${storage.percentage}%)`}
              >
                <HardDrive className={cn(
                  'h-4 w-4',
                  storage.percentage >= 90 ? 'text-red-500' :
                  storage.percentage >= 70 ? 'text-yellow-500' :
                  'text-surface-400'
                )} />
              </div>
            ) : (
              // Expanded: show full indicator
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-surface-500 dark:text-surface-400">
                  <span className="flex items-center gap-1.5">
                    <HardDrive className="h-3 w-3" />
                    Storage
                  </span>
                  <span>{storage.percentage}%</span>
                </div>
                <div className="h-1.5 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all', getStorageColor(storage.percentage))}
                    style={{ width: `${Math.min(100, storage.percentage)}%` }}
                  />
                </div>
                <div className="text-[10px] text-surface-400 dark:text-surface-500">
                  {storage.usedFormatted} / {storage.limitFormatted}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="hidden items-center justify-end border-t border-surface-200 p-2 dark:border-surface-800 lg:flex">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </aside>
  );
}

// ============================================
// Mobile Overlay
// ============================================

interface MobileOverlayProps {
  isVisible: boolean;
  onClick: () => void;
}

function MobileOverlay({ isVisible, onClick }: MobileOverlayProps) {
  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 top-12 z-30 bg-black/50 lg:hidden"
      onClick={onClick}
      aria-hidden="true"
    />
  );
}

// ============================================
// Main Content Area
// ============================================

interface MainContentProps {
  children?: React.ReactNode;
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  isRightRailCollapsed: boolean;
  onRightRailToggle: () => void;
  onOpenStatus: () => void;
}

function MainContent({
  children,
  activeTab,
  onTabChange,
  isRightRailCollapsed,
  onRightRailToggle,
  onOpenStatus,
}: MainContentProps) {
  const { openFiles, activeFilePath, openRequestNonce } = useFileStore();
  const hasOpenFiles = openFiles.length > 0;
  const [filePanelMode, setFilePanelMode] = useState<FilePanelMode>('minimized');
  const previousNonce = useRef(openRequestNonce);

  // Expand the viewer on an explicit open (openFile bumps the nonce - including
  // re-opening an already-open file, which leaves openFiles.length unchanged).
  // Passive activeFilePath changes (close/delete fallback, tab switches) don't
  // bump the nonce, so they don't pop a minimized viewer open.
  useEffect(() => {
    if (openRequestNonce !== previousNonce.current) {
      previousNonce.current = openRequestNonce;
      if (openFiles.length > 0) {
        setFilePanelMode('expanded');
      }
    }
  }, [openRequestNonce, openFiles.length]);

  // Closing the last file returns to chat.
  useEffect(() => {
    if (openFiles.length === 0) {
      setFilePanelMode('minimized');
    }
  }, [openFiles.length]);

  const showFileViewer = hasOpenFiles && filePanelMode === 'expanded';
  const railMode: SidebarTab = showFileViewer ? 'files' : activeTab;
  const activeFile = activeFilePath ? openFiles.find((file) => file.path === activeFilePath) : null;

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-surface-50 dark:bg-surface-950">
      <div
        className={cn(
          'min-h-0 flex-1 grid',
          isRightRailCollapsed ? 'xl:grid-cols-[minmax(0,1fr)_48px]' : 'xl:grid-cols-[minmax(0,1fr)_320px]'
        )}
      >
        <section className="min-h-0 overflow-hidden bg-white dark:bg-surface-950">
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            {/* Expanded: full tab strip + minimize control above the viewer */}
            {showFileViewer && (
              <div className="flex shrink-0 items-center border-b border-surface-200 bg-surface-50 dark:border-surface-800 dark:bg-surface-900">
                <div className="min-w-0 flex-1">
                  <FileTabs />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mx-2 h-8 w-8 shrink-0"
                  onClick={() => setFilePanelMode('minimized')}
                  title="Minimize file panel"
                  aria-label="Minimize file panel"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-hidden">
              {children ? (
                children
              ) : showFileViewer ? (
                <FileViewer />
              ) : (
                <ChatContainer className="h-full" onOpenStatus={onOpenStatus} />
              )}
            </div>

            {/* Minimized: compact bottom file tray (old desktop "Files N open" bar) */}
            {hasOpenFiles && !showFileViewer && (
              <button
                type="button"
                className="flex shrink-0 items-center gap-2 border-t border-surface-200 bg-surface-50 px-3 py-2 text-left text-xs text-surface-600 hover:bg-surface-100 hover:text-surface-900 dark:border-surface-800 dark:bg-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-100"
                onClick={() => setFilePanelMode('expanded')}
                title={activeFile ? `Expand ${activeFile.name}` : 'Expand file panel'}
              >
                <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">Files</span>
                <span className="text-surface-400 dark:text-surface-500">
                  {openFiles.length} open
                </span>
                {activeFile && (
                  <span className="min-w-0 truncate text-surface-400 dark:text-surface-500">
                    · {activeFile.name}
                  </span>
                )}
                <Maximize2 className="ml-auto h-3.5 w-3.5 shrink-0" />
              </button>
            )}
          </div>
        </section>

        <div className="hidden min-h-0 border-l border-surface-200 bg-white dark:border-surface-800 dark:bg-surface-900 xl:block">
          <RightRailColumn
            activeTab={railMode}
            isCollapsed={isRightRailCollapsed}
            onToggle={onRightRailToggle}
            onTabChange={onTabChange}
          />
        </div>
      </div>
    </main>
  );
}

function RightRailColumn({
  activeTab,
  isCollapsed,
  onToggle,
  onTabChange,
}: {
  activeTab: SidebarTab;
  isCollapsed: boolean;
  onToggle: () => void;
  onTabChange: (tab: SidebarTab) => void;
}) {
  if (isCollapsed) {
    return (
      <aside className="hidden min-h-0 justify-center border-l border-surface-200 bg-white py-3 dark:border-surface-800 dark:bg-surface-900 xl:flex">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle} title="Show context rail">
          <PanelRight className="h-4 w-4" />
        </Button>
      </aside>
    );
  }

  return (
    <ContextRail
      activeTab={activeTab}
      onTabChange={onTabChange}
      onCollapse={onToggle}
    />
  );
}

function ContextRail({
  activeTab,
  onTabChange,
  onCollapse,
}: {
  activeTab: SidebarTab;
  onTabChange?: (tab: SidebarTab) => void;
  onCollapse?: () => void;
}) {
  return (
    <aside data-active-tab={activeTab} className="flex h-full min-h-0 flex-col overflow-y-auto">
      <PinnedPanel onTabChange={onTabChange} onCollapse={onCollapse} />
      {activeTab === 'chat' && <ChatRunningContext onTabChange={onTabChange} />}
      {activeTab === 'chat' && <FileReferencePanel />}
    </aside>
  );
}

/** Max rows shown in the file-reference picker (at rest and per query). */
const FILE_PICKER_LIMIT = 10;

/**
 * Score `needle` against `haystack`: substring matches rank highest (earlier =
 * better), then in-order subsequence matches, then no match (0). Cheap enough
 * to run over every asset on each keystroke (ADR-0009 section 4 - client-side only).
 */
function fuzzyScore(haystack: string, needle: string): number {
  const idx = haystack.indexOf(needle);
  if (idx >= 0) return 1000 - idx;
  let h = 0;
  for (let n = 0; n < needle.length; n++) {
    while (h < haystack.length && haystack[h] !== needle[n]) h++;
    if (h >= haystack.length) return 0;
    h++;
  }
  return 1;
}

function rankAssets(assets: WorkspaceAsset[], query: string): WorkspaceAsset[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    // At rest: most-recently-updated, so the panel is useful before any typing.
    return [...assets].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, FILE_PICKER_LIMIT);
  }
  return assets
    .map((a) => ({ a, score: fuzzyScore(`${a.name} ${a.sourceFolder}`.toLowerCase(), q) }))
    .filter((s) => s.score > 0)
    .sort((x, y) => y.score - x.score)
    .slice(0, FILE_PICKER_LIMIT)
    .map((s) => s.a);
}

/** Workspace-relative path for an asset (sourceFolder is '' at the root). */
function assetRelativePath(asset: WorkspaceAsset): string {
  return asset.sourceFolder ? `${asset.sourceFolder}/${asset.name}` : asset.name;
}

/**
 * File-reference picker (ADR-0009). Chat-route-only rail panel: fuzzy search
 * over the user's workspace assets (uploads + dashboards). Clicking a row
 * inserts the backticked workspace-relative path at the composer's caret -
 * Claude reads backticked paths reliably via its Read tool, and the user never
 * has to recall or retype a path. Source is workspace_assets (no fresh FS
 * walk); fetched once on mount, filtered client-side.
 */
function FileReferencePanel() {
  const setPendingComposerInsert = useChatStore((s) => s.setPendingComposerInsert);
  const [assets, setAssets] = useState<WorkspaceAsset[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.workspace.assets
      .list()
      .then((res) => {
        if (cancelled) return;
        setAssets(res.assets.filter((a) => a.type === 'upload' || a.type === 'dashboard'));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load files');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(() => rankAssets(assets, query), [assets, query]);

  const handleSelect = (asset: WorkspaceAsset) => {
    setPendingComposerInsert(`\`${assetRelativePath(asset)}\``);
    setQuery('');
  };

  return (
    <section className="border-b border-surface-200 p-4 dark:border-surface-800">
      <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">Reference a file</h2>
      <p className="text-xs text-surface-500 dark:text-surface-400">
        Search files to reference in chat - click to insert path
      </p>

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search workspace files..."
          className="w-full rounded-lg border border-surface-200 bg-surface-50 py-2 pl-9 pr-3 text-sm text-surface-900 placeholder:text-surface-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-100"
        />
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border border-surface-200 dark:border-surface-700">
        {isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-surface-500 dark:text-surface-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading files...
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-rose-600 dark:text-rose-400">{error}</div>
        ) : results.length === 0 ? (
          <div className="p-4 text-sm text-surface-500 dark:text-surface-400">
            {query.trim() ? 'No matching files.' : 'No files in your workspace yet.'}
          </div>
        ) : (
          results.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => handleSelect(asset)}
              title={assetRelativePath(asset)}
              className="group flex w-full items-center gap-3 border-b border-surface-200 px-3 py-2.5 text-left last:border-b-0 hover:bg-surface-50 dark:border-surface-700 dark:hover:bg-surface-800"
            >
              <FileText className="h-4 w-4 shrink-0 text-surface-400 group-hover:text-primary-500" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-surface-800 dark:text-surface-100">{asset.name}</span>
                <span className="block truncate text-xs text-surface-500 dark:text-surface-400">
                  {asset.sourceFolder || 'workspace root'}
                </span>
              </span>
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function PinnedPanel({
  onTabChange,
  onCollapse,
}: {
  onTabChange?: (tab: SidebarTab) => void;
  onCollapse?: () => void;
}) {
  const pins = usePinsStore((s) => s.pins);
  const isLoaded = usePinsStore((s) => s.isLoaded);
  const isLoading = usePinsStore((s) => s.isLoading);
  const load = usePinsStore((s) => s.load);
  const selectSession = useChatStore((s) => s.selectSession);
  const openFile = useFileStore((s) => s.openFile);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      void load();
    }
  }, [isLoaded, isLoading, load]);

  const handleClick = async (pin: Pin) => {
    if (pin.kind === 'chat') {
      await selectSession(pin.entityId);
      onTabChange?.('chat');
    } else if (pin.kind === 'file') {
      try {
        await openFile(pin.entityId);
        onTabChange?.('files');
      } catch {
        onTabChange?.('files');
      }
    } else {
      // dashboard - open the workspace asset directly
      window.open(api.workspace.assets.getDownloadUrl(pin.entityId, true), '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section className="border-b border-surface-200 p-4 dark:border-surface-800">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">Pinned</h2>
          <p className="text-xs text-surface-500 dark:text-surface-400">Bookmark anything from Files, Dashboards, or chats</p>
        </div>
        {onCollapse && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCollapse} title="Hide context rail">
            <PanelRightClose className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="mt-4 rounded-lg border border-surface-200 dark:border-surface-700 overflow-hidden">
        {pins.length === 0 ? (
          <div className="p-4 text-sm text-surface-500 dark:text-surface-400">
            Click the bookmark icon on a file, dashboard, or chat to pin it here.
          </div>
        ) : (
          pins.map((pin) => (
            <button
              key={`${pin.kind}:${pin.entityId}`}
              type="button"
              onClick={() => void handleClick(pin)}
              className="group flex w-full items-center gap-3 border-b border-surface-200 px-3 py-3 text-left last:border-b-0 hover:bg-surface-50 dark:border-surface-700 dark:hover:bg-surface-800"
            >
              <Bookmark className="mt-0.5 h-4 w-4 shrink-0 fill-current text-primary-600 dark:text-primary-300" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-primary-700 dark:text-primary-300">{pin.label}</span>
                <span className="block truncate text-xs text-surface-500 dark:text-surface-400">
                  {pin.kind}{pin.sublabel ? ` - ${pin.sublabel}` : ''}
                </span>
              </span>
              <PinToggle kind={pin.kind} entityId={pin.entityId} />
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function ChatRunningContext({
  onTabChange,
}: {
  onTabChange?: (tab: SidebarTab) => void;
}) {
  const activeSessionId = useChatStore((s) => s.activeSessionId);
  // Another user's chat produced files in their own folder. Name the owner; never fetch them.
  const readOnlyOwnerName = useChatStore((s) => {
    const session = s.sessions.find((item) => item.id === s.activeSessionId);
    return session && !session.canPost ? session.ownerUsername : null;
  });
  const activeToolName = useChatStore((s) => s.activeToolName);
  const streamingState = useChatStore((s) => s.streamingState);
  const openFile = useFileStore((s) => s.openFile);

  const [produced, setProduced] = useState<ProducedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRefreshedFor = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!activeSessionId || readOnlyOwnerName !== null) {
      setProduced([]);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.chat.getProduced(activeSessionId);
      setProduced(result.produced);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load produced artifacts');
    } finally {
      setIsLoading(false);
    }
  }, [activeSessionId, readOnlyOwnerName]);

  // Initial load + reload when switching chat sessions.
  useEffect(() => {
    if (activeSessionId !== lastRefreshedFor.current) {
      lastRefreshedFor.current = activeSessionId;
      void refresh();
    }
  }, [activeSessionId, refresh]);

  // After a stream completes (back to idle), refetch - the chat run may have
  // written files and the lineage event log now has rows we haven't seen.
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && streamingState === 'idle') {
      void refresh();
    }
    wasStreaming.current = streamingState === 'streaming' || streamingState === 'connecting';
  }, [streamingState, refresh]);

  const handleClick = async (item: ProducedItem) => {
    if (!item.path.startsWith('/')) {
      try {
        await openFile(item.path);
        onTabChange?.('files');
      } catch {
        onTabChange?.('files');
      }
      return;
    }
    if (item.assetId) {
      window.open(api.workspace.assets.getDownloadUrl(item.assetId, true), '_blank', 'noopener,noreferrer');
      return;
    }
    onTabChange?.('files');
  };

  return (
    <section className="border-b border-surface-200 p-4 dark:border-surface-800">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">Produced by this chat</h2>
          <p className="text-xs text-surface-500 dark:text-surface-400">Files written or indexed during this conversation</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => void refresh()}
          disabled={isLoading || !activeSessionId || readOnlyOwnerName !== null}
          title="Refresh"
        >
          <RotateCcw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </Button>
      </div>

      {activeToolName && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700 dark:border-primary-900 dark:bg-primary-950/30 dark:text-primary-300">
          <Sparkles className="h-3.5 w-3.5 animate-pulse" />
          <span className="truncate">Running <span className="font-medium">{activeToolName}</span></span>
        </div>
      )}

      <div className="mt-3 rounded-lg border border-surface-200 dark:border-surface-700 overflow-hidden">
        {!activeSessionId ? (
          <div className="p-4 text-sm text-surface-500 dark:text-surface-400">Open a chat to see what it produces.</div>
        ) : readOnlyOwnerName !== null ? (
          <div className="flex items-center gap-2 p-4 text-sm text-surface-500 dark:text-surface-400">
            <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
            Owned by {readOnlyOwnerName}. Files from this chat stay in their folder.
          </div>
        ) : produced.length === 0 ? (
          <div className="p-4 text-sm text-surface-500 dark:text-surface-400">
            Nothing produced yet. Files written by this chat or indexed reports will appear here.
          </div>
        ) : (
          produced.map((item) => (
            <button
              key={`${item.source}:${item.path}`}
              type="button"
              onClick={() => void handleClick(item)}
              className="flex w-full items-start gap-3 border-b border-surface-200 px-3 py-3 text-left last:border-b-0 hover:bg-surface-50 dark:border-surface-700 dark:hover:bg-surface-800"
            >
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-primary-700 dark:text-primary-300">{item.label}</span>
                <span className="block truncate text-xs text-surface-500 dark:text-surface-400">{item.sublabel}</span>
              </span>
            </button>
          ))
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
    </section>
  );
}

function ProductPageShell({
  action,
  children,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full overflow-auto p-4 lg:p-5">
      <div className="max-w-7xl mx-auto space-y-5">
        {action && (
          <div className="flex items-center justify-end gap-4">
          {action}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ============================================
// Status page
// ============================================

type StatusDotTone = 'ok' | 'warn' | 'error' | 'unknown';

function StatusDot({ tone }: { tone: StatusDotTone }) {
  const color =
    tone === 'ok' ? 'bg-emerald-500'
    : tone === 'warn' ? 'bg-amber-500'
    : tone === 'error' ? 'bg-red-500'
    : 'bg-surface-400 dark:bg-surface-600';
  return <span className={cn('h-2 w-2 rounded-full shrink-0', color)} />;
}

function StatusRow({
  tone,
  label,
  value,
  detail,
}: {
  tone: StatusDotTone;
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <StatusDot tone={tone} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-surface-900 dark:text-surface-100">{label}</div>
          <div className="text-xs font-medium text-surface-600 dark:text-surface-400 truncate">{value}</div>
        </div>
        {detail && <div className="mt-0.5 text-xs text-surface-500 dark:text-surface-400 truncate">{detail}</div>}
      </div>
    </div>
  );
}

function StatusCard({
  title,
  loading,
  error,
  onRefresh,
  action,
  children,
}: {
  title: string;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-surface-200 dark:border-surface-700">
        <div className="text-sm font-medium text-surface-900 dark:text-surface-100">{title}</div>
        <div className="flex items-center gap-2">
          {action}
          {onRefresh && (
            <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading} title="Refresh">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          )}
        </div>
      </div>
      {error ? (
        <div className="p-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      ) : (
        <div className="divide-y divide-surface-200 dark:divide-surface-700">{children}</div>
      )}
    </div>
  );
}

function formatUptime(seconds: number) {
  if (!seconds || seconds < 0) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatusModal({
  isOpen,
  onClose,
  isAdmin,
  onOpenSettings,
}: {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  onOpenSettings: (section?: SettingsSection) => void;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative flex h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl dark:bg-surface-900">
        <div className="flex items-center justify-between border-b border-surface-200 px-5 py-3 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100">Status</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close status">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <StatusPage isAdmin={isAdmin} onOpenSettings={onOpenSettings} />
        </div>
      </div>
    </div>
  );
}

function StatusPage({
  isAdmin,
  onOpenSettings,
}: {
  isAdmin: boolean;
  onOpenSettings: (section?: SettingsSection) => void;
}) {
  const [claude, setClaude] = useState<ClaudeAuthStatus | null>(null);
  const [codex, setCodex] = useState<CodexAuthStatus | null>(null);
  const [system, setSystem] = useState<SystemStatus | null>(null);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [systemLoading, setSystemLoading] = useState(true);
  const [systemError, setSystemError] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    setProviderLoading(true);
    setProviderError(null);
    try {
      const [c, x] = await Promise.all([api.claude.getStatus(), api.codex.getStatus()]);
      setClaude(c);
      setCodex(x);
    } catch (err) {
      setProviderError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setProviderLoading(false);
    }
  }, []);

  const loadSystem = useCallback(async () => {
    setSystemLoading(true);
    setSystemError(null);
    try {
      const s = await api.system.getStatus();
      setSystem(s);
    } catch (err) {
      setSystemError(err instanceof Error ? err.message : 'Failed to load system status');
    } finally {
      setSystemLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
    if (isAdmin) loadSystem();
  }, [isAdmin, loadProviders, loadSystem]);

  const claudeTone: StatusDotTone =
    !claude ? 'unknown'
    : !claude.cliInstalled ? 'error'
    : claude.authenticated ? 'ok'
    : claude.error ? 'error'
    : 'warn';

  const codexTone: StatusDotTone =
    !codex ? 'unknown'
    : !codex.cliInstalled ? 'error'
    : codex.authenticated ? 'ok'
    : codex.error ? 'error'
    : 'warn';

  return (
    <ProductPageShell>
      <div className="space-y-4">
        <StatusCard
          title="AI Providers"
          loading={providerLoading}
          error={providerError}
          onRefresh={loadProviders}
          action={
            <Button variant="outline" size="sm" onClick={() => onOpenSettings('claude')}>
              Manage
            </Button>
          }
        >
          <StatusRow
            tone={claudeTone}
            label="Claude"
            value={
              !claude ? 'Loading...'
              : !claude.cliInstalled ? 'CLI not installed'
              : claude.authenticated ? 'Connected'
              : claude.error || 'Not connected'
            }
            detail={claude?.accountEmail || (system?.claude.runtime ? `Runtime: ${system.claude.runtime}` : undefined)}
          />
          <StatusRow
            tone={codexTone}
            label="Codex"
            value={
              !codex ? 'Loading...'
              : !codex.cliInstalled ? 'CLI not installed'
              : codex.authenticated ? 'Connected'
              : codex.error || 'Not connected'
            }
          />
        </StatusCard>

        {isAdmin && (
          <StatusCard
            title="System"
            loading={systemLoading}
            error={systemError}
            onRefresh={loadSystem}
          >
            {!system ? (
              <div className="p-4 text-sm text-surface-500 dark:text-surface-400">Loading...</div>
            ) : (
              <>
                <StatusRow
                  tone={system.status === 'ok' ? 'ok' : 'error'}
                  label="Server"
                  value={system.status === 'ok' ? 'Healthy' : 'Degraded'}
                  detail={`${system.environment} - ${system.responseTime}`}
                />
                <StatusRow
                  tone="ok"
                  label="Uptime"
                  value={formatUptime(system.server.uptime)}
                  detail={`Node ${system.server.nodeVersion} - ${system.server.platform}/${system.server.arch}`}
                />
                <StatusRow
                  tone={system.server.memory.free / system.server.memory.total < 0.1 ? 'warn' : 'ok'}
                  label="Memory"
                  value={`${system.server.memory.used} / ${system.server.memory.total} MB`}
                  detail={`${system.server.memory.free} MB free`}
                />
                <StatusRow
                  tone={system.dataDirectory.writable ? 'ok' : 'error'}
                  label="Data directory"
                  value={system.dataDirectory.writable ? 'Writable' : 'Read-only / missing'}
                  detail={system.dataDirectory.path}
                />
              </>
            )}
          </StatusCard>
        )}
      </div>
    </ProductPageShell>
  );
}

/**
 * File viewer container - renders the appropriate viewer based on file type
 */
function FileViewer() {
  const { activeFilePath, getViewerType, getFileByPath } = useFileStore();
  const activeFile = activeFilePath ? getFileByPath(activeFilePath) : null;
  const viewerType: FileViewerType | null = activeFilePath ? getViewerType(activeFilePath) : null;

  if (!activeFile) return <MainContentPlaceholder />;

  // Loading state
  if (activeFile.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-surface-600 dark:text-surface-400">Loading {activeFile.name}...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (activeFile.error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 h-full">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-destructive-100 dark:bg-destructive-900/30 text-destructive-600 dark:text-destructive-400 mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2">
            Failed to load file
          </h2>
          <p className="text-destructive-600 dark:text-destructive-400">
            {activeFile.error}
          </p>
        </div>
      </div>
    );
  }

  const handleDownload = () => {
    void downloadWorkspaceFile(activeFile.path, activeFile.name);
  };

  let viewer: React.ReactNode;

  // Code viewer (supports code and text file types)
  if (viewerType === 'code' || viewerType === 'text') {
    viewer = (
      <CodeViewer
        content={activeFile.content || ''}
        filename={activeFile.name}
        showLineNumbers={true}
      />
    );
  } else if (viewerType === 'html') {
    // HTML viewer (code + preview in browser)
    viewer = (
      <HtmlViewer
        content={activeFile.content || ''}
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'csv') {
    // CSV viewer (TanStack Table with sorting and filtering)
    viewer = (
      <CsvViewer
        content={activeFile.content || ''}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'excel') {
    // Excel viewer (xlsx with sheet navigation, sorting, filtering)
    viewer = (
      <ExcelViewer
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'parquet') {
    viewer = (
      <ParquetViewer
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'pcap') {
    // PCAP viewer (network packet analysis with tshark)
    viewer = (
      <PcapViewer
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'markdown') {
    // Markdown editor (split-pane with live preview and autosave)
    viewer = (
      <MarkdownEditor
        path={activeFile.path}
        content={activeFile.content || ''}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'image') {
    // Image viewer (zoom, pan, fit)
    viewer = (
      <ImageViewer
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else if (viewerType === 'pdf') {
    // PDF viewer (pdfjs-dist with page navigation and zoom)
    viewer = (
      <PdfViewer
        path={activeFile.path}
        filename={activeFile.name}
      />
    );
  } else {
    // Binary viewer for unsupported file types
    viewer = (
      <BinaryViewer
        path={activeFile.path}
        filename={activeFile.name}
        size={activeFile.size}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-surface-950">
      <div className="flex shrink-0 items-center gap-2 border-b border-surface-200 bg-surface-50 px-3 py-2 dark:border-surface-800 dark:bg-surface-900">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-700 dark:text-surface-200" title={activeFile.path}>
          {activeFile.name}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDownload}
          className="h-8 gap-1.5"
          title="Download file"
        >
          <Download className="h-4 w-4" />
          <span className="text-xs">Download</span>
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {viewer}
      </div>
    </div>
  );
}

function MainContentPlaceholder() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <TelcoOSLogo className="w-16 h-16 rounded-2xl mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2">
          Welcome to TelcoOS
        </h2>
        <p className="text-surface-600 dark:text-surface-400 mb-4">
          Telecom Data Analytics Platform
        </p>
        <p className="text-sm text-surface-500 dark:text-surface-500">
          Select a file from the sidebar or start a new chat session to begin.
        </p>
      </div>
    </div>
  );
}

// ============================================
// Layout Component
// ============================================

export function Layout({ children }: LayoutProps) {
  // Auth state
  const { user } = useAuthStore();
  const isAdmin = user?.isAdmin || false;
  const isSupportAdmin = Boolean(user?.isSupportAdmin);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>('chat');
  const [isRightRailCollapsed, setIsRightRailCollapsed] = useState(false);

  // Upload modal state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadDestination, setUploadDestination] = useState('');
  // Callback the current upload-trigger asked us to run after a successful upload.
  // Files-tab uses this to refetch its listing; other triggers leave it null.
  const uploadOnSuccessRef = useRef<(() => void | Promise<void>) | null>(null);

  // Settings state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('app');

  // Status modal state (formerly a sidebar tab; now a header-icon modal)
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isSipnavApiMapOpen, setIsSipnavApiMapOpen] = useState(false);

  // Skills Catalog open state lives in chatStore so the composer's
  // Sparkles button can toggle it without prop drilling.
  const isCatalogOpen = useChatStore((s) => s.isCatalogOpen);
  const setIsCatalogOpen = useChatStore((s) => s.setIsCatalogOpen);

  // Handlers
  const handleMenuClick = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleTabChange = useCallback((tab: SidebarTab) => {
    setActiveTab(tab);
  }, []);

  const handleSidebarCollapse = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const handleOverlayClick = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const handleUploadClick = useCallback((destination?: string, onSuccess?: () => void | Promise<void>) => {
    setUploadDestination(destination ?? '');
    uploadOnSuccessRef.current = onSuccess ?? null;
    setIsUploadOpen(true);
  }, []);

  const handleUploadClose = useCallback(() => {
    setIsUploadOpen(false);
    uploadOnSuccessRef.current = null;
  }, []);

  const handleUploaded = useCallback(async () => {
    await uploadOnSuccessRef.current?.();
  }, []);

  // Settings handlers
  const handleSettingsOpen = useCallback((section?: SettingsSection) => {
    if (section) {
      setSettingsSection(section);
    }
    setIsSettingsOpen(true);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const handleStatusOpen = useCallback(() => {
    setIsStatusOpen(true);
  }, []);

  const handleStatusClose = useCallback(() => {
    setIsStatusOpen(false);
  }, []);

  const handleStatusOpenSettings = useCallback((section?: SettingsSection) => {
    setIsStatusOpen(false);
    handleSettingsOpen(section);
  }, [handleSettingsOpen]);

  const handleCatalogClose = useCallback(() => {
    setIsCatalogOpen(false);
  }, [setIsCatalogOpen]);

  const handleCatalogOpenSettings = useCallback(() => {
    handleSettingsOpen('skills');
  }, [handleSettingsOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      // Cmd+, to open settings
      if (isCtrlOrCmd && event.key === ',') {
        event.preventDefault();
        setIsSettingsOpen(true);
        return;
      }

      // Cmd/Ctrl+K opens the Skills Catalog (works from any tab; switch
      // to chat first so the composer is ready for the pre-filled command).
      if (isCtrlOrCmd && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setActiveTab('chat');
        setIsCatalogOpen(true);
        return;
      }

      // Escape closes settings.
      if (event.key === 'Escape') {
        if (isSettingsOpen) {
          event.preventDefault();
          setIsSettingsOpen(false);
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSettingsOpen, setIsCatalogOpen]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-50 dark:bg-surface-950">
      <Header
        onMenuClick={handleMenuClick}
        isSidebarOpen={isSidebarOpen}
        onSettingsClick={() => handleSettingsOpen()}
        onStatusClick={handleStatusOpen}
        onSipnavApiMapClick={() => setIsSipnavApiMapOpen(true)}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <MobileOverlay isVisible={isSidebarOpen} onClick={handleOverlayClick} />

        <Sidebar
          isOpen={isSidebarOpen}
          isCollapsed={isSidebarCollapsed}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onUploadClick={handleUploadClick}
          onCollapse={handleSidebarCollapse}
        />

        <MainContent
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isRightRailCollapsed={isRightRailCollapsed}
          onRightRailToggle={() => setIsRightRailCollapsed((prev) => !prev)}
          onOpenStatus={handleStatusOpen}
        >
          {children}
        </MainContent>
      </div>

      {/* File upload modal */}
      <FileUpload
        isOpen={isUploadOpen}
        onClose={handleUploadClose}
        destination={uploadDestination}
        onUploaded={handleUploaded}
      />

      {/* Settings panel */}
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={handleSettingsClose}
        initialSection={settingsSection}
      />

      {/* Skills Catalog (Cmd/Ctrl+K or composer Sparkles button) */}
      <SkillsCatalog
        isOpen={isCatalogOpen}
        onClose={handleCatalogClose}
        onOpenSettings={isAdmin ? handleCatalogOpenSettings : undefined}
      />

      {/* Status modal (header-icon trigger) */}
      <StatusModal
        isOpen={isStatusOpen}
        onClose={handleStatusClose}
        isAdmin={isAdmin}
        onOpenSettings={handleStatusOpenSettings}
      />

      {isSupportAdmin && (
        <SipnavApiMapModal
          isOpen={isSipnavApiMapOpen}
          onClose={() => setIsSipnavApiMapOpen(false)}
        />
      )}

    </div>
  );
}

export default Layout;
